import { normalise, rotateAbout, slerp, cross, sub, scale, dot, add } from '../geo/vec3.js';
import { geodesic, isCounterClockwise, toLatLon, toVec3 } from '../geo/sphere.js';
/**
 * MAP-069. A hand-drawn outline is too smooth to read as a coast. This adds
 * bays and headlands by seeded midpoint displacement, so the silhouette stays
 * exactly where it was drawn while the edge gains character.
 *
 * Deterministic by construction: the same art and seed always give the same
 * coast, on every device, on every reload. A world that reshuffles itself is
 * not a place.
 */
/** A small, fast, seedable PRNG (mulberry32). */
export function makeRandom(seed) {
    let state = seed >>> 0;
    return () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let t = state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** Mixes a string into a numeric seed so keys give stable, distinct worlds. */
export function hashSeed(...parts) {
    let h = 2166136261;
    for (const part of parts) {
        const text = String(part);
        for (let i = 0; i < text.length; i += 1) {
            h ^= text.charCodeAt(i);
            h = Math.imul(h, 16777619);
        }
    }
    return h >>> 0;
}
/**
 * Displace the midpoint of each edge perpendicular to it, along the sphere,
 * halving the displacement at each level.
 */
export function detailRing(ring, options) {
    const subdivisions = options.subdivisions ?? 3;
    const amplitude = options.amplitude ?? 0.18;
    const persistence = options.persistence ?? 0.5;
    let current = [...ring];
    for (let level = 0; level < subdivisions; level += 1) {
        const random = makeRandom(options.seed + level * 7919);
        const next = [];
        const levelAmplitude = amplitude * persistence ** level;
        for (let i = 0; i < current.length; i += 1) {
            const a = current[i];
            const b = current[(i + 1) % current.length];
            next.push(a);
            const mid = slerp(a, b, 0.5);
            const edgeLength = Math.acos(Math.max(-1, Math.min(1, dot(a, b))));
            if (edgeLength < 1e-6)
                continue;
            // Perpendicular to the edge, tangent to the sphere at the midpoint.
            const along = normalise(sub(b, scale(mid, dot(b, mid))));
            const outward = normalise(cross(mid, along));
            // Clamped to well under half the edge: a displacement that large folds
            // the coastline over itself, and a self-intersecting ring triangulates
            // into overlapping shards.
            const maxOffset = edgeLength * 0.35;
            const offset = Math.max(-maxOffset, Math.min(maxOffset, (random() * 2 - 1) * levelAmplitude * edgeLength));
            next.push(normalise(rotateAbout(mid, normalise(cross(mid, outward)), offset)));
        }
        current = next;
    }
    return current;
}
/**
 * `variant` asks for a different coast on the same landmass: 0 is the one it
 * has always had, so existing worlds are untouched until someone regenerates.
 */
export function detailLandmass(mass, worldSeed, variant = 0) {
    const authored = mass.ring.map(([lat, lon]) => toVec3({ lat, lon }));
    // A new coast on the old outline is too subtle to notice, so a variant grows
    // a whole new silhouette of about the same size in the same place.
    const outline = variant === 0 ? authored : reshapeRing(authored, hashSeed(worldSeed, mass.key, '~', variant));
    const ring = detailRing(outline, {
        seed: variant === 0 ? hashSeed(worldSeed, mass.key) : hashSeed(worldSeed, mass.key, '~', variant),
        amplitude: 0.08 + 0.22 * mass.roughness,
    });
    return { key: mass.key, name: mass.name, ring, authored };
}
/**
 * A seeded blob standing where `ring` stands: a circle of the ring's mean
 * radius, bent by a few low harmonics so it reads as a landmass rather than a
 * coin, wound the same way as the ring it replaces.
 */
export function reshapeRing(ring, seed) {
    const centre = normalise(ring.reduce((acc, v) => add(acc, v), { x: 0, y: 0, z: 0 }));
    const radius = ring.reduce((sum, v) => sum + Math.acos(Math.max(-1, Math.min(1, dot(v, centre)))), 0) / ring.length;
    const random = makeRandom(seed);
    const pole = Math.abs(centre.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
    const u = normalise(cross(pole, centre));
    const w = cross(centre, u);
    const harmonics = [2, 3, 4, 5].map((k) => ({
        k,
        amp: (0.28 / k) * (0.4 + random() * 0.9),
        phase: random() * Math.PI * 2,
    }));
    // Stretched along one random axis, as most continents are.
    const stretch = 0.75 + random() * 0.5;
    const tilt = random() * Math.PI;
    const points = 48;
    const out = [];
    for (let i = 0; i < points; i += 1) {
        const theta = (i / points) * Math.PI * 2;
        const bend = harmonics.reduce((sum, h) => sum + h.amp * Math.cos(h.k * theta + h.phase), 0);
        const oval = 1 / Math.sqrt(Math.cos(theta - tilt) ** 2 / stretch + Math.sin(theta - tilt) ** 2 * stretch);
        const rho = Math.min(1.2, radius * oval * (1 + bend));
        const dir = add(scale(u, Math.cos(theta)), scale(w, Math.sin(theta)));
        out.push(normalise(add(scale(centre, Math.cos(rho)), scale(dir, Math.sin(rho)))));
    }
    return isCounterClockwise(out) === isCounterClockwise(ring) ? out : out.reverse();
}
/** How far the detailed coast wanders from the line that was drawn. */
export function maximumDeviation(detailed) {
    const authoredLatLon = detailed.authored.map(toLatLon);
    let worst = 0;
    for (const point of detailed.ring) {
        const p = toLatLon(point);
        let nearest = Infinity;
        for (const a of authoredLatLon)
            nearest = Math.min(nearest, geodesic(p, a));
        worst = Math.max(worst, nearest);
    }
    return worst;
}
export { add };
//# sourceMappingURL=coastline.js.map